import { createElement, lazy, useSyncExternalStore, type ComponentType, type LazyExoticComponent } from 'react';
import { t } from '@bms2/shared';
import { AsyncStatePanel } from '../components/composites/AsyncStatePanel';
import { reportCaughtInBrowser } from './systemErrors';

/**
 * 🔒 A route group's pages load on the group's first visit, from one chunk per
 * group (piece 12; decision 10580). `lazyGroup` takes the group's one dynamic
 * import and hands back a function that names a page in it, so every page of the
 * group shares the one request and the one chunk the bundler emits for it.
 *
 * 🔨 A chunk a deploy removed is answered by the page's one guarded reload
 * (decision 1460), and Vite's import then resolves to nothing. The page is never
 * drawn from nothing: the import stays pending, so the outlet's loading state
 * holds until the reload replaces the page.
 *
 * 🔨 A failure while the browser reports no connection is never reloaded, which
 * would land on the browser's own error page. The page area says the connection
 * is down, and its Try again asks for the group again.
 *
 * 🔨 A failure while the browser reports a connection, after the one guarded reload
 * is spent, is reported as a fault and draws a sentence naming the load in the page
 * area, with Try again; the shell's generic error is for a page that failed to draw
 * and not for a part of the application that failed to arrive (T3601).
 */
export function lazyGroup<Group extends object>(load: () => Promise<Group | undefined>, online: () => boolean = () => typeof navigator === 'undefined' || navigator.onLine !== false) {
  const settled = () => load().then((group) => group ?? new Promise<Group>(() => undefined));
  return function page<Name extends keyof Group>(name: Name): ComponentType<PropsOf<Group, Name>> {
    type Props = PropsOf<Group, Name>;
    const listeners = new Set<() => void>();
    const retry = () => {
      current = arrive();
      listeners.forEach((changed) => changed());
    };
    const arrive = (): LazyExoticComponent<ComponentType<Props>> =>
      lazy(async () => {
        try {
          return { default: (await settled())[name] as unknown as ComponentType<Props> };
        } catch (failed) {
          if (!online()) return { default: () => <GroupUnavailable kind="offline" onRetry={retry} /> };
          reportCaughtInBrowser('lazyGroup.load', failed);
          return { default: () => <GroupUnavailable kind="failed" onRetry={retry} /> };
        }
      });
    let current = arrive();
    const subscribe = (changed: () => void) => {
      listeners.add(changed);
      return () => void listeners.delete(changed);
    };
    function GroupPage(props: Props) {
      // The lazy component is made once per attempt, outside any render; this only reads which attempt stands.
      const arrived = useSyncExternalStore(subscribe, () => current, () => current);
      return createElement(arrived as unknown as ComponentType<object>, props as object);
    }
    return GroupPage;
  };
}

/** What the page area draws when a group could not load: the connection is down, or the group failed to arrive with a connection. */
function GroupUnavailable({ kind, onRetry }: { kind: 'offline' | 'failed'; onRetry: () => void }) {
  return (
    <div data-shell-group-offline={kind === 'offline' ? 'true' : undefined} data-shell-group-failed={kind === 'failed' ? 'true' : undefined}>
      <AsyncStatePanel state={{ kind: 'failed', error: t(kind === 'offline' ? 'shell.groupOffline' : 'shell.groupFailed') }} emptyAction="" onRetry={onRetry}>
        {() => null}
      </AsyncStatePanel>
    </div>
  );
}

/** The props a group module's page takes, read off the component it exports under a name. */
type PropsOf<Group, Name extends keyof Group> = Group[Name] extends ComponentType<infer Props> ? Props : never;
